// src/utils/errorUtils.ts
/**
 * Utility functions for parsing and formatting error messages
 * into user-friendly display text.
 * 
 * This module handles errors from ALL AI providers:
 * - Google Gemini
 * - Venice AI
 * - OpenAI (future)
 * - Anthropic (future)
 */

/**
 * Known error patterns and their user-friendly messages
 */
const ERROR_PATTERNS: Array<{
  pattern: RegExp;
  message: string;
  category: 'api_key' | 'rate_limit' | 'network' | 'content' | 'server' | 'quota' | 'model' | 'auth' | 'unknown';
}> = [
  // ============================================================================
  // API Key / Authentication errors (all providers)
  // ============================================================================
  {
    pattern: /API[_\s]?key[_\s]?(expired|invalid|missing)/i,
    message: "The API key has expired or is invalid. Please contact the administrator to update the API configuration.",
    category: 'api_key'
  },
  {
    pattern: /API_KEY_INVALID|invalid[_\s]?api[_\s]?key/i,
    message: "The API key is no longer valid. Please contact the administrator to renew the API key.",
    category: 'api_key'
  },
  {
    pattern: /not[_\s]?initialized|check[_\s]?api[_\s]?key/i,
    message: "The AI service is not configured. Please check the API settings.",
    category: 'api_key'
  },
  {
    pattern: /unauthorized|401|authentication[_\s]?failed/i,
    message: "Authentication failed. Please check your API credentials.",
    category: 'auth'
  },
  {
    pattern: /forbidden|403/i,
    message: "Access denied. Your account may not have permission for this action.",
    category: 'auth'
  },
  
  // ============================================================================
  // Rate limiting & Quota (all providers)
  // ============================================================================
  {
    pattern: /rate[_\s]?limit|too[_\s]?many[_\s]?requests|429/i,
    message: "Too many requests. Please wait a moment and try again.",
    category: 'rate_limit'
  },
  {
    pattern: /402|payment[_\s]?required/i,
    message: "Sorry - the AI service is currently very busy. Please try again later or contact support.",
    category: 'quota'
  },
  {
    pattern: /quota[_\s]?exceeded|resource[_\s]?exhausted|billing/i,
    message: "Usage quota has been exceeded. Please try again later or contact support.",
    category: 'quota'
  },
  {
    pattern: /insufficient[_\s]?(credits|balance|funds)/i,
    message: "Insufficient credits to complete this request.",
    category: 'quota'
  },
  
  // ============================================================================
  // Network errors (all providers)
  // ============================================================================
  {
    pattern: /network[_\s]?error|fetch[_\s]?failed|connection[_\s]?(refused|timeout|reset)/i,
    message: "Unable to connect to the AI service. Please check your internet connection and try again.",
    category: 'network'
  },
  {
    pattern: /timeout|timed[_\s]?out|ETIMEDOUT/i,
    message: "The request took too long. Please try again.",
    category: 'network'
  },
  {
    pattern: /ENOTFOUND|ECONNREFUSED|ECONNRESET|ERR_NETWORK/i,
    message: "Unable to reach the AI service. Please check your connection and try again.",
    category: 'network'
  },
  {
    pattern: /failed[_\s]?to[_\s]?fetch|network[_\s]?request[_\s]?failed/i,
    message: "Network request failed. Please check your connection and try again.",
    category: 'network'
  },
  {
    pattern: /Failed to send a request to the Edge Function/i,
    message: "Unable to reach the AI service. Please check your connection and try again.",
    category: 'network'
  },
  
  // ============================================================================
  // Content/Safety errors (all providers)
  // ============================================================================
  {
    pattern: /content[_\s]?blocked|safety|harmful|blocked.*reason|content[_\s]?filter/i,
    message: "Your request couldn't be processed due to content restrictions. Please try rephrasing your request.",
    category: 'content'
  },
  {
    pattern: /RECITATION/i,
    message: "The AI couldn't generate a unique response. Please try with different input.",
    category: 'content'
  },
  {
    pattern: /moderation|policy[_\s]?violation|inappropriate/i,
    message: "The content was flagged by moderation. Please modify your request.",
    category: 'content'
  },
  
  // ============================================================================
  // Server errors (all providers)
  // ============================================================================
  {
    pattern: /500|internal[_\s]?server[_\s]?error/i,
    message: "The AI service is experiencing issues. Please try again in a few moments.",
    category: 'server'
  },
  {
    pattern: /503|service[_\s]?unavailable|temporarily[_\s]?unavailable/i,
    message: "The AI service is temporarily unavailable. Please try again later.",
    category: 'server'
  },
  {
    pattern: /502|bad[_\s]?gateway/i,
    message: "There's a temporary server issue. Please try again.",
    category: 'server'
  },
  {
    pattern: /504|gateway[_\s]?timeout/i,
    message: "The server took too long to respond. Please try again.",
    category: 'server'
  },
  {
    pattern: /overloaded|capacity|busy/i,
    message: "The AI service is currently busy. Please try again in a few moments.",
    category: 'server'
  },
  
  // ============================================================================
  // Model-specific errors
  // ============================================================================
  {
    pattern: /model.*not[_\s]?found|unsupported[_\s]?model|invalid[_\s]?model/i,
    message: "The selected AI model is not available. Please try a different option.",
    category: 'model'
  },
  {
    pattern: /model.*deprecated|model.*retired/i,
    message: "The selected AI model is no longer available. Please select a different model in settings.",
    category: 'model'
  },
  {
    pattern: /context[_\s]?(length|limit)|too[_\s]?long|max[_\s]?tokens/i,
    message: "The request was too long. Please try with shorter input.",
    category: 'model'
  },
  
  // ============================================================================
  // Provider-specific patterns
  // ============================================================================
  // Gemini
  {
    pattern: /\[GoogleGenerativeAI[_\s]?Error\]/i,
    message: "The Google AI service encountered an error. Please try again.",
    category: 'server'
  },
  {
    pattern: /gemini.*error|generativelanguage\.googleapis/i,
    message: "There was an issue with the Google AI service. Please try again.",
    category: 'server'
  },
  // Venice
  {
    pattern: /Venice[_\s]?API[_\s]?(error|failed)/i,
    message: "The AI chef is taking a break — please try again in a moment.",
    category: 'server'
  },
  {
    pattern: /venice\.ai/i,
    message: "The AI chef is taking a break — please try again in a moment.",
    category: 'server'
  },
  // OpenAI (future-proofing)
  {
    pattern: /openai[_\s]?(error|api)/i,
    message: "The OpenAI service encountered an error. Please try again.",
    category: 'server'
  },
  // Anthropic (future-proofing)
  {
    pattern: /anthropic|claude[_\s]?(error|api)/i,
    message: "The Anthropic service encountered an error. Please try again.",
    category: 'server'
  },
];

/**
 * Parses an error message (potentially containing JSON or technical details)
 * and returns a user-friendly version
 */
export function formatErrorForUser(rawError: string | Error): string {
  const errorString = rawError instanceof Error ? rawError.message : rawError;
  
  if (!errorString) {
    return "An unexpected error occurred. Please try again.";
  }
  
  // Check against known patterns
  for (const { pattern, message } of ERROR_PATTERNS) {
    if (pattern.test(errorString)) {
      return message;
    }
  }
  
  // If the error contains JSON (like the one in the screenshot), extract a clean message
  if (errorString.includes('[{') || errorString.includes('{"')) {
    // Try to find a user-friendly portion before the JSON
    const beforeJson = errorString.split(/\[?\{/)[0].trim();
    if (beforeJson && beforeJson.length > 20 && beforeJson.length < 200) {
      // Clean up the prefix if it exists
      const cleaned = beforeJson
        .replace(/^(Error:|Failed:)\s*/i, '')
        .replace(/:\s*$/, '')
        .trim();
      if (cleaned.length > 10) {
        return cleaned + ". Please try again.";
      }
    }
    
    // Fallback for JSON-heavy errors
    return "The AI service encountered an error. Please try again later.";
  }
  
  // If the error is too long (likely contains technical details), truncate it
  if (errorString.length > 150) {
    // Try to get the first meaningful sentence
    const firstSentence = errorString.match(/^[^.!?]+[.!?]/);
    if (firstSentence && firstSentence[0].length > 20) {
      return firstSentence[0].trim();
    }
    return "An error occurred while processing your request. Please try again.";
  }
  
  // For short, clean errors, return as-is but clean up common prefixes
  const cleanedError = errorString
    .replace(/^(Error:|Failed:|Exception:)\s*/i, '')
    .replace(/\[GoogleGenerativeAI Error\]:\s*/i, '')
    .replace(/\[.*Error\]:\s*/i, '')
    .replace(/^(Gemini|Venice|OpenAI|Anthropic)\s*(API)?\s*(call)?\s*(failed|error)?:?\s*/i, '')
    .replace(/Error fetching from https?:\/\/[^\s:]+:?\s*/i, '')
    .trim();
  
  return cleanedError || "An unexpected error occurred. Please try again.";
}

/**
 * Gets additional context/suggestion based on error category
 */
export function getErrorSuggestion(rawError: string | Error): string | null {
  const errorString = rawError instanceof Error ? rawError.message : rawError;
  
  if (!errorString) return null;
  
  for (const { pattern, category } of ERROR_PATTERNS) {
    if (pattern.test(errorString)) {
      switch (category) {
        case 'api_key':
          return "This is a configuration issue that requires administrator attention.";
        case 'auth':
          return "Check that your API credentials are correct and active.";
        case 'rate_limit':
          return "Wait about 30 seconds before trying again.";
        case 'quota':
          return "Daily usage limits may have been reached.";
        case 'network':
          return "Check your internet connection and refresh the page.";
        case 'content':
          return "Try using different words or a simpler description.";
        case 'server':
          return "The service may be under heavy load. Try again in a few minutes.";
        case 'model':
          return "Check model settings in the admin panel or try a different model.";
        default:
          return null;
      }
    }
  }
  
  return null;
}

/**
 * Maps an API error to a user-friendly message while preserving the original
 * error on the returned object for upstream handling (no logging).
 */
export function handleApiError(
  error: unknown,
  _context: string = 'API call'
): { userMessage: string; suggestion: string | null; originalError: unknown } {
  const errorMessage = error instanceof Error ? error.message : String(error);

  return {
    userMessage: formatErrorForUser(errorMessage),
    suggestion: getErrorSuggestion(errorMessage),
    originalError: error
  };
}

/**
 * Quick helper to get a user-friendly error message from any error type
 * Use this in catch blocks for consistent error messaging
 * 
 * @example
 * catch {
 *   setError(friendlyError(error));
 * }
 */
export function friendlyError(error: unknown): string {
  if (error instanceof Error) {
    return formatErrorForUser(error.message);
  }
  if (typeof error === 'string') {
    return formatErrorForUser(error);
  }
  return "An unexpected error occurred. Please try again.";
}
